import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useBible } from '../contexts/BibleContext';
import { useAnnotations } from '../contexts/AnnotationContext';
import { useTheme, FONT_SIZES } from '../contexts/ThemeContext';
import Icon from '../components/UI/Icon';
import BookSelector from '../components/Reader/BookSelector';
import ChapterSelector from '../components/Reader/ChapterSelector';
import TranslationSelector from '../components/Reader/TranslationSelector';
import AnnotationPanel from '../components/Reader/AnnotationPanel';
import db from '../services/database';
import { getBookName, getChapterCount, getBibleShareUrl } from '../constants/bibleConstants';
import './ReaderPage.css';

// Map abbreviated book codes to OSIS codes (same as PlansPage)
const BOOK_CODE_TO_OSIS = {
  'GEN': 'Gen', 'EXO': 'Exod', 'LEV': 'Lev', 'NUM': 'Num', 'DEU': 'Deut',
  'JOS': 'Josh', 'JDG': 'Judg', 'RUT': 'Ruth', '1SA': '1Sam', '2SA': '2Sam',
  '1KI': '1Kgs', '2KI': '2Kgs', '1CH': '1Chr', '2CH': '2Chr', 'EZR': 'Ezra',
  'NEH': 'Neh', 'EST': 'Esth', 'JOB': 'Job', 'PSA': 'Ps', 'PRO': 'Prov',
  'ECC': 'Eccl', 'SNG': 'Song', 'ISA': 'Isa', 'JER': 'Jer', 'LAM': 'Lam',
  'EZK': 'Ezek', 'DAN': 'Dan', 'HOS': 'Hos', 'JOL': 'Joel', 'AMO': 'Amos',
  'OBA': 'Obad', 'JON': 'Jonah', 'MIC': 'Mic', 'NAM': 'Nah', 'HAB': 'Hab',
  'ZEP': 'Zeph', 'HAG': 'Hag', 'ZEC': 'Zech', 'MAL': 'Mal',
  'MAT': 'Matt', 'MRK': 'Mark', 'LUK': 'Luke', 'JHN': 'John', 'ACT': 'Acts',
  'ROM': 'Rom', '1CO': '1Cor', '2CO': '2Cor', 'GAL': 'Gal', 'EPH': 'Eph',
  'PHP': 'Phil', 'COL': 'Col', '1TH': '1Thess', '2TH': '2Thess', '1TI': '1Tim',
  '2TI': '2Tim', 'TIT': 'Titus', 'PHM': 'Phlm', 'HEB': 'Heb', 'JAS': 'Jas',
  '1PE': '1Pet', '2PE': '2Pet', '1JN': '1John', '2JN': '2John', '3JN': '3John',
  'JUD': 'Jude', 'REV': 'Rev'
};

// Bible books with chapter counts for milestone calculation
const BIBLE_BOOKS = [
  { name: 'Genesis', chapters: 50 }, { name: 'Exodus', chapters: 40 }, { name: 'Leviticus', chapters: 27 },
  { name: 'Numbers', chapters: 36 }, { name: 'Deuteronomy', chapters: 34 }, { name: 'Joshua', chapters: 24 },
  { name: 'Judges', chapters: 21 }, { name: 'Ruth', chapters: 4 }, { name: '1 Samuel', chapters: 31 },
  { name: '2 Samuel', chapters: 24 }, { name: '1 Kings', chapters: 22 }, { name: '2 Kings', chapters: 25 },
  { name: '1 Chronicles', chapters: 29 }, { name: '2 Chronicles', chapters: 36 }, { name: 'Ezra', chapters: 10 },
  { name: 'Nehemiah', chapters: 13 }, { name: 'Esther', chapters: 10 }, { name: 'Job', chapters: 42 },
  { name: 'Psalms', chapters: 150 }, { name: 'Proverbs', chapters: 31 }, { name: 'Ecclesiastes', chapters: 12 },
  { name: 'Song of Solomon', chapters: 8 }, { name: 'Isaiah', chapters: 66 }, { name: 'Jeremiah', chapters: 52 },
  { name: 'Lamentations', chapters: 5 }, { name: 'Ezekiel', chapters: 48 }, { name: 'Daniel', chapters: 12 },
  { name: 'Hosea', chapters: 14 }, { name: 'Joel', chapters: 3 }, { name: 'Amos', chapters: 9 },
  { name: 'Obadiah', chapters: 1 }, { name: 'Jonah', chapters: 4 }, { name: 'Micah', chapters: 7 },
  { name: 'Nahum', chapters: 3 }, { name: 'Habakkuk', chapters: 3 }, { name: 'Zephaniah', chapters: 3 },
  { name: 'Haggai', chapters: 2 }, { name: 'Zechariah', chapters: 14 }, { name: 'Malachi', chapters: 4 },
  { name: 'Matthew', chapters: 28 }, { name: 'Mark', chapters: 16 }, { name: 'Luke', chapters: 24 },
  { name: 'John', chapters: 21 }, { name: 'Acts', chapters: 28 }, { name: 'Romans', chapters: 16 },
  { name: '1 Corinthians', chapters: 16 }, { name: '2 Corinthians', chapters: 13 }, { name: 'Galatians', chapters: 6 },
  { name: 'Ephesians', chapters: 6 }, { name: 'Philippians', chapters: 4 }, { name: 'Colossians', chapters: 4 },
  { name: '1 Thessalonians', chapters: 5 }, { name: '2 Thessalonians', chapters: 3 }, { name: '1 Timothy', chapters: 6 },
  { name: '2 Timothy', chapters: 4 }, { name: 'Titus', chapters: 3 }, { name: 'Philemon', chapters: 1 },
  { name: 'Hebrews', chapters: 13 }, { name: 'James', chapters: 5 }, { name: '1 Peter', chapters: 5 },
  { name: '2 Peter', chapters: 3 }, { name: '1 John', chapters: 5 }, { name: '2 John', chapters: 1 },
  { name: '3 John', chapters: 1 }, { name: 'Jude', chapters: 1 }, { name: 'Revelation', chapters: 22 }
];

// Get milestone target for a given day
function getMilestoneTarget(books, duration, dayNumber) {
  const totalChapters = books.reduce((sum, book) => sum + book.chapters, 0);
  const chaptersPerDay = totalChapters / duration;
  const targetChapter = Math.ceil(dayNumber * chaptersPerDay);
  
  let chapterCount = 0;
  for (const book of books) {
    if (chapterCount + book.chapters >= targetChapter) {
      const chapterInBook = targetChapter - chapterCount;
      return { book: book.name, chapter: Math.min(chapterInBook, book.chapters) };
    }
    chapterCount += book.chapters;
  }
  
  const lastBook = books[books.length - 1];
  return { book: lastBook.name, chapter: lastBook.chapters };
}

// Calculate milestones for a plan
function calculateMilestones(plan) {
  if (!plan.startDate) return [];

  const startDate = new Date(plan.startDate);
  const milestones = [];
  const isDaily = plan.duration <= 31;
  
  let books;
  const isBibleInYear = plan.presetId === 'bible-year' || plan.name === 'Bible in a Year';
  const isNewTestament = plan.presetId === 'new-testament-90' || plan.name === 'New Testament in 90 Days';
  const isGospels = plan.presetId === 'gospels-30' || plan.name === 'Gospels in 30 Days';
  const isPsalms = plan.presetId === 'psalms-month' || plan.name === 'Psalms in a Month';
  const isProverbs = plan.presetId === 'proverbs-month' || plan.name === 'Proverbs in a Month';
  
  if (isBibleInYear) {
    books = BIBLE_BOOKS;
  } else if (isNewTestament) {
    books = BIBLE_BOOKS.slice(39);
  } else if (isGospels) {
    books = BIBLE_BOOKS.slice(39, 43);
  } else if (isPsalms) {
    books = [{ name: 'Psalms', chapters: 150 }];
  } else if (isProverbs) {
    books = [{ name: 'Proverbs', chapters: 31 }];
  } else if (plan.books && plan.books.length > 0) {
    books = plan.books.map(bookCode => {
      const osisCode = BOOK_CODE_TO_OSIS[bookCode] || bookCode;
      return { name: getBookName(osisCode), chapters: getChapterCount(osisCode) };
    });
  } else {
    return [];
  }

  if (isDaily) {
    for (let day = 1; day <= plan.duration; day++) {
      const milestoneDate = new Date(startDate);
      milestoneDate.setDate(milestoneDate.getDate() + day - 1);
      const target = getMilestoneTarget(books, plan.duration, day);
      milestones.push({ period: day, day, date: milestoneDate, target: `${target.book} ${target.chapter}`, book: target.book, chapter: target.chapter, isDaily: true });
    }
  } else {
    const totalWeeks = Math.ceil(plan.duration / 7);
    for (let week = 1; week <= totalWeeks; week++) {
      const dayNumber = Math.min(week * 7, plan.duration);
      const milestoneDate = new Date(startDate);
      milestoneDate.setDate(milestoneDate.getDate() + dayNumber - 1);
      const target = getMilestoneTarget(books, plan.duration, dayNumber);
      milestones.push({ period: week, week, day: dayNumber, date: milestoneDate, target: `${target.book} ${target.chapter}`, book: target.book, chapter: target.chapter, isDaily: false });
    }
  }

  return milestones;
}

// Get cumulative chapter number across all books
function getGlobalChapterNumber(bookName, chapter, booksList) {
  let total = 0;
  for (const book of booksList) {
    if (book.name === bookName) {
      return total + chapter;
    }
    total += book.chapters;
  }
  return total;
}

function ReaderPage() {
  const { bookId, chapterId, verseId } = useParams();
  
  const {
    isLoading,
    books,
    currentBook,
    currentChapter,
    chapterContent,
    selectedTranslations,
    viewMode,
    setViewMode,
    goToBook,
    goToChapter,
    goToNextChapter,
    goToPreviousChapter,
    getTranslationInfo,
    getChaptersList
  } = useBible();
  
  const { getHighlightsForChapter, getNotesForChapter, isEarmarked, toggleEarmark } = useAnnotations();
  const { fontSize, setFontSize } = useTheme();
  
  // Local state
  const [showBookSelector, setShowBookSelector] = useState(false);
  const [showChapterSelector, setShowChapterSelector] = useState(false);
  const [showTranslationSelector, setShowTranslationSelector] = useState(false);
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [selectedVerseRange, setSelectedVerseRange] = useState(null); // For multi-verse selection { start, end, bibleId }
  const [rangeCopied, setRangeCopied] = useState(false); // Show "Copied!" feedback
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [initialAnnotationTab, setInitialAnnotationTab] = useState('highlight');
  const [textToSpeechActive, setTextToSpeechActive] = useState(false);
  const [showTextSizeMenu, setShowTextSizeMenu] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  const [highlightedVerses, setHighlightedVerses] = useState([]); // For shared verse highlighting (array for ranges)
  
  // Refs for sync scroll
  const scrollContainerRefs = useRef({});
  const isScrolling = useRef(false);
  const hasNavigatedFromUrl = useRef(false); // Track if we've navigated from URL params
  const hasHighlightedVerse = useRef(false); // Track if we've done initial verse highlight
  
  // Get chapters list for current book
  const chapters = getChaptersList();

  // Handle verse highlighting from URL - ONLY on initial load
  useEffect(() => {
    // Only highlight once on initial load
    if (hasHighlightedVerse.current) return;
    
    if (verseId && !isLoading && currentBook && currentChapter) {
      // Parse verse range (e.g., "16-18" or just "16")
      const rangeParts = verseId.split('-');
      const startVerse = parseInt(rangeParts[0]);
      const endVerse = rangeParts[1] ? parseInt(rangeParts[1]) : startVerse;
      
      if (!isNaN(startVerse) && !isNaN(endVerse)) {
        hasHighlightedVerse.current = true;
        
        // Create array of verse numbers to highlight
        const versesToHighlight = [];
        for (let v = startVerse; v <= endVerse; v++) {
          versesToHighlight.push(v);
        }
        setHighlightedVerses(versesToHighlight);
        
        // Scroll to the first highlighted verse after content loads
        setTimeout(() => {
          const verseElement = document.querySelector(`[data-verse="${startVerse}"]`);
          if (verseElement) {
            verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 300);
        
        // Clear highlight after 5 seconds
        setTimeout(() => setHighlightedVerses([]), 5000);
      }
    }
  }, [verseId, isLoading, currentBook, currentChapter]);

  // Load active reading plan
  useEffect(() => {
    async function loadActivePlan() {
      try {
        const plans = await db.readingPlans.toArray();
        const active = plans.find(p => p.isActive);
        setActivePlan(active || null);
      } catch (err) {
        console.error('Failed to load active plan:', err);
      }
    }
    loadActivePlan();
  }, []);

  // Scroll to top when chapter or book changes (unless we have a specific verse to highlight)
  useEffect(() => {
    if (!verseId || hasHighlightedVerse.current) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [currentBook?.id, currentChapter, verseId]);
  
  // Navigate to book/chapter from URL params
  // Only runs when bookId/chapterId/books change - NOT when currentBook/currentChapter change
  useEffect(() => {
    if (!bookId || books.length === 0) return;
    
    // Case-insensitive book lookup
    const book = books.find(b => b.id.toLowerCase() === bookId.toLowerCase());
    if (!book) {
      console.warn('Book not found:', bookId);
      return;
    }
    
    const chapterNum = chapterId ? parseInt(chapterId) : 1;
    
    console.log('URL navigation: navigating to', book.id, chapterNum);
    
    // Mark BEFORE navigating so URL update effect skips
    hasNavigatedFromUrl.current = true;
    
    // Navigate to URL-specified location with book and chapter together
    // This avoids race condition where chapter content loads before chapter is set
    goToBook(book.id, chapterNum);
    
    // After navigation, allow URL updates again after a delay
    setTimeout(() => {
      hasNavigatedFromUrl.current = false;
    }, 200);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookId, chapterId, books]);
  
  // Update URL when navigation changes from button clicks
  // Use history.replaceState to avoid triggering React Router re-renders
  useEffect(() => {
    // Skip while navigating from URL
    if (hasNavigatedFromUrl.current) {
      console.log('URL update: skipping (navigating from URL)');
      return;
    }
    
    if (currentBook?.id && currentChapter) {
      const safeBookId = currentBook.id.replace(/[^a-zA-Z0-9]/g, '');
      const safeChapter = parseInt(currentChapter, 10);
      
      if (safeBookId && !isNaN(safeChapter) && safeChapter > 0) {
        const expectedPath = `/reader/${safeBookId}/${safeChapter}`;
        const currentPath = window.location.pathname;
        
        // Strip verse params for comparison
        const currentBasePath = currentPath.replace(/\/reader\/([^/]+)\/(\d+).*/, '/reader/$1/$2');
        
        console.log('URL update check:', { currentBasePath, expectedPath, willUpdate: currentBasePath !== expectedPath });
        
        if (currentBasePath !== expectedPath) {
          console.log('URL update: updating to', expectedPath);
          window.history.replaceState(null, '', expectedPath);
        }
      }
    }
  }, [currentBook?.id, currentChapter]);
  
  // Sync scroll handler
  const handleScroll = useCallback((sourceId, event) => {
    if (isScrolling.current) return;
    isScrolling.current = true;
    
    const sourceContainer = event.target;
    const scrollRatio = sourceContainer.scrollTop / (sourceContainer.scrollHeight - sourceContainer.clientHeight);
    
    Object.entries(scrollContainerRefs.current).forEach(([id, container]) => {
      if (id !== sourceId && container) {
        const targetScrollTop = scrollRatio * (container.scrollHeight - container.clientHeight);
        container.scrollTop = targetScrollTop;
      }
    });
    
    setTimeout(() => {
      isScrolling.current = false;
    }, 50);
  }, []);
  
  // Handle verse click - supports multi-verse selection
  const handleVerseClick = useCallback((verseId, bibleId, verseNum, openToNote = false) => {
    // If in range selection mode, extend the range
    if (selectedVerseRange) {
      const start = selectedVerseRange.start;
      setSelectedVerseRange({
        ...selectedVerseRange,
        end: Math.max(start, verseNum),
        start: Math.min(start, verseNum)
      });
      return;
    }
    
    // Normal single verse selection
    setSelectedVerse({ verseId, bibleId, verseNum });
    setInitialAnnotationTab(openToNote ? 'note' : 'highlight');
    setShowAnnotationPanel(true);
  }, [selectedVerseRange]);
  
  // Start multi-verse selection
  const handleStartRangeSelection = useCallback(() => {
    if (selectedVerse) {
      setSelectedVerseRange({
        start: selectedVerse.verseNum,
        end: selectedVerse.verseNum,
        bibleId: selectedVerse.bibleId,
        bookId: currentBook?.id,
        chapter: currentChapter
      });
      setShowAnnotationPanel(false);
    }
  }, [selectedVerse, currentBook, currentChapter]);
  
  // Copy verse range
  const handleCopyRange = useCallback(async () => {
    if (!selectedVerseRange) return;
    
    const { start, end, bibleId, bookId, chapter } = selectedVerseRange;
    const chapterData = chapterContent[bibleId];
    if (!chapterData?.verses) return;
    
    // Get the book info from the stored bookId (not currentBook which may have changed)
    const book = books?.find(b => b.id === bookId);
    const bookName = book?.name || bookId;
    
    // Get verses in range
    const versesInRange = chapterData.verses.filter(v => v.verse >= start && v.verse <= end);
    const verseText = versesInRange.map(v => `${v.verse} ${v.text}`).join(' ');
    
    // Get translation info
    const translationInfo = getTranslationInfo?.(bibleId);
    const translationName = translationInfo?.abbreviation || translationInfo?.name || bibleId;
    
    // Build reference using stored bookId and chapter
    const reference = start === end 
      ? `${bookName} ${chapter}:${start}`
      : `${bookName} ${chapter}:${start}-${end}`;
    
    const verseParam = start === end ? start : `${start}-${end}`;
    const shareUrl = getBibleShareUrl(bookId, chapter, verseParam);
    
    // Format: "Reference - Version\nVerse text\nLink"
    const formattedText = `${reference} - ${translationName}\n${verseText}\n${shareUrl}`;
    
    try {
      await navigator.clipboard.writeText(formattedText);
      // Show "Copied!" feedback
      setRangeCopied(true);
      // Clear selection after a brief delay to show feedback
      setTimeout(() => {
        setSelectedVerseRange(null);
        setRangeCopied(false);
      }, 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [selectedVerseRange, books, chapterContent, getTranslationInfo]);
  
  // Cancel range selection
  const handleCancelRange = useCallback(() => {
    setSelectedVerseRange(null);
  }, []);
  
  // Text-to-speech
  const toggleTextToSpeech = useCallback(() => {
    if (textToSpeechActive) {
      window.speechSynthesis.cancel();
      setTextToSpeechActive(false);
    } else {
      const content = chapterContent[selectedTranslations[0]]?.verses;
      if (content && content.length > 0) {
        const text = content.map(v => `${v.verse}. ${v.text}`).join(' ');
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.9;
        utterance.onend = () => setTextToSpeechActive(false);
        window.speechSynthesis.speak(utterance);
        setTextToSpeechActive(true);
      }
    }
  }, [textToSpeechActive, chapterContent, selectedTranslations]);
  
  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft' && !e.ctrlKey && !e.metaKey) {
        goToPreviousChapter();
      } else if (e.key === 'ArrowRight' && !e.ctrlKey && !e.metaKey) {
        goToNextChapter();
      } else if (e.key === 'Escape') {
        setShowAnnotationPanel(false);
        setSelectedVerse(null);
        setShowTextSizeMenu(false);
      }
    };
    
    // Close menus when clicking outside
    const handleClickOutside = (e) => {
      if (showTextSizeMenu && !e.target.closest('.text-size-control')) {
        setShowTextSizeMenu(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [goToNextChapter, goToPreviousChapter, showTextSizeMenu]);
  
  // Handle earmark toggle
  const handleEarmarkToggle = useCallback((bibleId) => {
    if (!currentBook || !currentChapter) return;
    const chapterId = `${currentBook.id}.${currentChapter}`;
    toggleEarmark(chapterId, bibleId, currentBook.name, currentChapter);
  }, [currentBook, currentChapter, toggleEarmark]);
  
  // Check if current chapter is earmarked
  const isCurrentChapterEarmarked = useCallback((bibleId) => {
    if (!currentBook || !currentChapter) return false;
    const chapterId = `${currentBook.id}.${currentChapter}`;
    return isEarmarked(chapterId, bibleId);
  }, [currentBook, currentChapter, isEarmarked]);

  // Render chapter content with highlights
  const renderChapterContent = useCallback((bibleId) => {
    const chapter = chapterContent[bibleId];
    if (!chapter?.verses || chapter.verses.length === 0) {
      if (chapter?.error) {
        return <div className="chapter-error">Error loading: {chapter.error}</div>;
      }
      return <div className="chapter-loading">Loading...</div>;
    }
    
    const chapterRef = `${currentBook?.id}.${currentChapter}`;
    const highlights = getHighlightsForChapter(chapterRef, bibleId);
    const notes = getNotesForChapter(chapterRef, bibleId);
    
    // Create a map of highlighted verses
    const highlightMap = {};
    highlights.forEach(h => {
      highlightMap[h.verseId] = h.color;
    });
    
    const noteMap = {};
    notes.forEach(n => {
      noteMap[n.verseId] = true;
    });
    
    return (
      <div className="chapter-content verses-container">
        {chapter.verses.map(verse => {
          const highlightColor = highlightMap[verse.id];
          const hasNote = noteMap[verse.id];
          const isSharedHighlight = highlightedVerses.includes(verse.verse);
          const isInSelectedRange = selectedVerseRange && 
            selectedVerseRange.bibleId === bibleId &&
            verse.verse >= selectedVerseRange.start && 
            verse.verse <= selectedVerseRange.end;
          
          return (
            <span 
              key={verse.id}
              className={`verse ${highlightColor ? `highlight-${highlightColor}` : ''} ${hasNote ? 'has-note' : ''} ${isSharedHighlight ? 'shared-highlight' : ''} ${isInSelectedRange ? 'range-selected' : ''}`}
              data-verse-id={verse.id}
              data-verse={verse.verse}
              onClick={(e) => handleVerseClick(verse.id, bibleId, verse.verse, false)}
            >
              <sup className="verse-num">{verse.verse}</sup>
              <span className="verse-text">{verse.text}</span>
              {hasNote && (
                <Icon 
                  name="note" 
                  size={14} 
                  className="note-indicator" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVerseClick(verse.id, bibleId, verse.verse, true);
                  }}
                />
              )}
              {' '}
            </span>
          );
        })}
      </div>
    );
  }, [chapterContent, currentBook, currentChapter, getHighlightsForChapter, getNotesForChapter, handleVerseClick, highlightedVerses, selectedVerseRange]);
  
  return (
    <div className="reader-page">
      {/* Reader Header */}
      <header className="reader-header">
        <div className="reader-header-left">
          <button 
            className="btn btn-ghost"
            onClick={() => setShowBookSelector(true)}
          >
            <span className="current-location">
              {currentBook?.name || 'Select Book'} {currentChapter || ''}
            </span>
            <Icon name="chevronDown" size={16} />
          </button>
        </div>
        
        <div className="reader-header-center">
          <button 
            className="btn btn-ghost btn-icon"
            onClick={goToPreviousChapter}
            title="Previous Chapter"
          >
            <Icon name="chevronLeft" size={20} />
          </button>
          <button 
            className="chapter-indicator-btn"
            onClick={() => setShowChapterSelector(true)}
            title="Select Chapter"
          >
            {currentChapter} / {currentBook?.chapters || 0}
          </button>
          <button 
            className="btn btn-ghost btn-icon"
            onClick={goToNextChapter}
            title="Next Chapter"
          >
            <Icon name="chevronRight" size={20} />
          </button>
        </div>
        
        <div className="reader-header-right">
          <button 
            className={`btn btn-ghost btn-icon ${textToSpeechActive ? 'active' : ''}`}
            onClick={toggleTextToSpeech}
            title={textToSpeechActive ? 'Stop Reading' : 'Read Aloud'}
          >
            <Icon name={textToSpeechActive ? 'pause' : 'speaker'} size={28} />
          </button>
          
          {/* Text Size Control */}
          <div className="text-size-control">
            <button 
              className="btn btn-ghost btn-icon"
              onClick={() => setShowTextSizeMenu(!showTextSizeMenu)}
              title="Text Size"
            >
              <Icon name="textSize" size={28} />
            </button>
            {showTextSizeMenu && (
              <div className="text-size-menu">
                <div className="text-size-header">Text Size</div>
                {Object.entries(FONT_SIZES).map(([key, config]) => (
                  <button
                    key={key}
                    className={`text-size-option ${fontSize === key ? 'active' : ''}`}
                    onClick={() => {
                      setFontSize(key);
                      setShowTextSizeMenu(false);
                    }}
                  >
                    <span className="size-preview" style={{ fontSize: `${config.base}px` }}>Aa</span>
                    <span className="size-name">{config.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <button 
            className="btn btn-ghost btn-icon"
            onClick={() => setShowTranslationSelector(true)}
            title="Translations"
          >
            <Icon name="book" size={28} />
          </button>
          
          <div className="view-mode-toggle">
            <button 
              className={`btn btn-ghost btn-icon ${viewMode === 'single' ? 'active' : ''}`}
              onClick={() => setViewMode('single')}
              title="Single View"
            >
              <Icon name="book" size={22} />
            </button>
            <button 
              className={`btn btn-ghost btn-icon ${viewMode === 'parallel' ? 'active' : ''}`}
              onClick={() => setViewMode('parallel')}
              title="Parallel View"
            >
              <Icon name="grid" size={22} />
            </button>
          </div>
        </div>
      </header>
      
      {/* Reader Content */}
      <div className={`reader-content view-${viewMode}`}>
        {isLoading ? (
          <div className="reader-loading">
            <div className="loading-spinner" />
            <p>Loading Scripture...</p>
          </div>
        ) : (
          <>
            {(viewMode === 'single' ? [selectedTranslations[0]] : selectedTranslations).map((bibleId, index) => {
              const translationInfo = getTranslationInfo(bibleId);
              
              return (
                <div 
                  key={bibleId}
                  className="translation-pane"
                  ref={el => scrollContainerRefs.current[bibleId] = el}
                  onScroll={(e) => viewMode === 'parallel' && handleScroll(bibleId, e)}
                >
                  {viewMode === 'parallel' && (
                    <div className="pane-header">
                      <span className="translation-name">
                        {translationInfo?.abbreviation || translationInfo?.name || bibleId}
                      </span>
                    </div>
                  )}
                  
                  <div className="scripture-container paper-texture">
                    {/* Earmark button */}
                    <button 
                      className={`earmark-btn ${isCurrentChapterEarmarked(bibleId) ? 'active' : ''}`}
                      onClick={() => handleEarmarkToggle(bibleId)}
                      title={isCurrentChapterEarmarked(bibleId) ? 'Remove earmark' : 'Earmark this chapter'}
                    >
                      <div className="earmark-fold" />
                    </button>
                    
                    <div className="chapter-header">
                      <h1 className="chapter-title">
                        {currentBook?.name}
                      </h1>
                      <span className="chapter-number">
                        Chapter {currentChapter}
                      </span>
                      {viewMode === 'single' && (
                        <span className="translation-indicator">
                          {translationInfo?.abbreviation || translationInfo?.name || bibleId}
                        </span>
                      )}
                    </div>
                    
                    {renderChapterContent(bibleId)}

                    {/* Update progress in my plan button */}
                    {activePlan && (
                      <div className="plan-progress-section">
                        <button
                          className="btn btn-secondary update-plan-btn"
                          onClick={async () => {
                            try {
                              // Save current position
                              const currentPosition = {
                                bookId: currentBook?.id,
                                bookName: currentBook?.name,
                                chapter: currentChapter
                              };
                              
                              // Calculate milestones and mark all completed up to current position
                              const milestones = calculateMilestones(activePlan);
                              const isDaily = activePlan.duration <= 31;
                              const key = isDaily ? 'completedDays' : 'completedWeeks';
                              
                              // Get the books list for this plan to calculate global chapter position
                              let planBooks;
                              const isBibleInYear = activePlan.presetId === 'bible-year' || activePlan.name === 'Bible in a Year';
                              const isNewTestament = activePlan.presetId === 'new-testament-90' || activePlan.name === 'New Testament in 90 Days';
                              const isGospels = activePlan.presetId === 'gospels-30' || activePlan.name === 'Gospels in 30 Days';
                              const isPsalms = activePlan.presetId === 'psalms-month' || activePlan.name === 'Psalms in a Month';
                              const isProverbs = activePlan.presetId === 'proverbs-month' || activePlan.name === 'Proverbs in a Month';
                              
                              if (isBibleInYear) {
                                planBooks = BIBLE_BOOKS;
                              } else if (isNewTestament) {
                                planBooks = BIBLE_BOOKS.slice(39);
                              } else if (isGospels) {
                                planBooks = BIBLE_BOOKS.slice(39, 43);
                              } else if (isPsalms) {
                                planBooks = [{ name: 'Psalms', chapters: 150 }];
                              } else if (isProverbs) {
                                planBooks = [{ name: 'Proverbs', chapters: 31 }];
                              } else if (activePlan.books && activePlan.books.length > 0) {
                                planBooks = activePlan.books.map(bookCode => {
                                  const osisCode = BOOK_CODE_TO_OSIS[bookCode] || bookCode;
                                  return { name: getBookName(osisCode), chapters: getChapterCount(osisCode) };
                                });
                              } else {
                                planBooks = BIBLE_BOOKS;
                              }
                              
                              // Get current reading position in global chapter numbers
                              const currentGlobalChapter = getGlobalChapterNumber(currentBook?.name, currentChapter, planBooks);
                              
                              // Find all milestones that should be marked complete
                              const periodsToComplete = [];
                              for (const milestone of milestones) {
                                const milestoneGlobalChapter = getGlobalChapterNumber(milestone.book, milestone.chapter, planBooks);
                                if (currentGlobalChapter >= milestoneGlobalChapter) {
                                  periodsToComplete.push(milestone.period);
                                }
                              }
                              
                              // Merge with existing completed periods (don't remove any)
                              const existingCompleted = activePlan[key] || [];
                              const newCompleted = [...new Set([...existingCompleted, ...periodsToComplete])].sort((a, b) => a - b);
                              
                              const updates = {
                                currentPosition,
                                [key]: newCompleted,
                                lastUpdated: new Date().toISOString()
                              };
                              
                              await db.readingPlans.update(activePlan.id, updates);
                              setActivePlan(prev => ({ ...prev, ...updates }));
                              
                              // Show confirmation with count
                              const newlyCompleted = periodsToComplete.filter(p => !existingCompleted.includes(p)).length;
                              const btn = document.querySelector('.update-plan-btn');
                              if (btn) {
                                const originalHTML = btn.innerHTML;
                                btn.textContent = newlyCompleted > 0 
                                  ? `✓ Marked ${newlyCompleted} ${isDaily ? 'day' : 'week'}${newlyCompleted > 1 ? 's' : ''} complete!`
                                  : '✓ Progress Saved!';
                                btn.disabled = true;
                                setTimeout(() => {
                                  btn.innerHTML = originalHTML;
                                  btn.disabled = false;
                                }, 2000);
                              }
                            } catch (err) {
                              console.error('Failed to update plan progress:', err);
                            }
                          }}
                        >
                          <Icon name="check" size={16} /> Update Progress in My Plan
                        </button>
                        <span className="plan-name-hint">
                          {activePlan.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      
      {/* Mobile Navigation - Bottom of page */}
      {window.innerWidth < 768 && !isLoading && (
        <div className="mobile-reader-nav">
          <button 
            className="mobile-nav-btn mobile-nav-prev"
            onClick={goToPreviousChapter}
            disabled={!currentBook || currentChapter <= 1}
            title="Previous Chapter"
          >
            <Icon name="chevronLeft" size={24} />
            <span>Previous</span>
          </button>
          
          <div className="mobile-nav-chapter">
            <button 
              className="chapter-indicator-btn"
              onClick={() => setShowChapterSelector(true)}
              title="Select Chapter"
            >
              {currentChapter} / {currentBook?.chapters || 0}
            </button>
          </div>
          
          <button 
            className="mobile-nav-btn mobile-nav-next"
            onClick={goToNextChapter}
            disabled={!currentBook || currentChapter >= currentBook.chapters}
            title="Next Chapter"
          >
            <span>Next</span>
            <Icon name="chevronRight" size={24} />
          </button>
        </div>
      )}
      
      {/* Modals */}
      {showBookSelector && (
        <BookSelector
          books={books}
          chapters={chapters}
          currentBook={currentBook}
          currentChapter={currentChapter}
          onSelectBook={(bookId) => {
            goToBook(bookId);
            setShowBookSelector(false);
          }}
          onSelectChapter={(chapterId) => {
            goToChapter(chapterId);
            setShowBookSelector(false);
          }}
          onClose={() => setShowBookSelector(false)}
        />
      )}
      
      {showChapterSelector && currentBook && (
        <ChapterSelector
          bookName={currentBook.name}
          totalChapters={currentBook.chapters}
          currentChapter={currentChapter}
          onSelectChapter={(chapter) => {
            goToChapter(chapter);
          }}
          onClose={() => setShowChapterSelector(false)}
        />
      )}
      
      {showTranslationSelector && (
        <TranslationSelector
          onClose={() => setShowTranslationSelector(false)}
        />
      )}
      
      {showAnnotationPanel && selectedVerse && (
        <AnnotationPanel
          verseId={selectedVerse.verseId}
          bibleId={selectedVerse.bibleId}
          verseNum={selectedVerse.verseNum}
          initialTab={initialAnnotationTab}
          onClose={() => {
            setShowAnnotationPanel(false);
            setSelectedVerse(null);
          }}
          onStartRangeSelection={handleStartRangeSelection}
        />
      )}
      
      {/* Range Selection Floating Bar */}
      {selectedVerseRange && (
        <div className={`range-selection-bar ${rangeCopied ? 'copied' : ''}`}>
          <div className="range-selection-info">
            <span className="range-label">
              {rangeCopied 
                ? 'Copied!'
                : selectedVerseRange.start === selectedVerseRange.end 
                  ? `Verse ${selectedVerseRange.start}` 
                  : `Verses ${selectedVerseRange.start}-${selectedVerseRange.end}`}
            </span>
            {!rangeCopied && <span className="range-hint">Tap another verse to extend</span>}
          </div>
          {!rangeCopied && (
            <div className="range-selection-actions">
              <button 
                className="btn btn-primary btn-sm"
                onClick={handleCopyRange}
              >
                <Icon name="copy" size={16} />
                Copy
              </button>
              <button 
                className="btn btn-ghost btn-sm"
                onClick={handleCancelRange}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ReaderPage;
